import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faDownload, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import './ImportFarmsModal.css';
import { API_BASE_URL } from '../constants/api';

interface ImportFarmsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onDataRefresh?: () => Promise<void>; // Callback to refresh data after import
  businessUnitId?: string;
  businessUnitName?: string;
  userToken: string;
  isProcessing?: boolean;
}

const ImportFarmsModal: React.FC<ImportFarmsModalProps> = ({
  isOpen,
  onClose,
  onDataRefresh,
  businessUnitId,
  businessUnitName,
  userToken,
  isProcessing = false
}) => {
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [error, setError] = useState('');
  const [importType, setImportType] = useState<'johnDeere' | 'caseAsf' | 'excel'>('johnDeere');
  const [processingProgress, setProcessingProgress] = useState('');
  const [processingResults, setProcessingResults] = useState<any>(null);

  // Reset progress and results when modal opens
  useEffect(() => {
    if (isOpen) {
      setProcessingProgress('');
      setProcessingResults(null);
      setError('');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      const file = files[0];
      const isValidFile = importType === 'excel' 
        ? (file.name.endsWith('.xlsx') || file.name.endsWith('.xls'))
        : file.name.endsWith('.zip');
      
      if (isValidFile) {
        setUploadedFile(file);
        setError('');
      } else {
        const expectedFormat = importType === 'excel' ? 'Excel file (.xlsx or .xls)' : 'ZIP file';
        setError(`Please upload a ${expectedFormat}`);
      }
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const isValidFile = importType === 'excel' 
        ? (file.name.endsWith('.xlsx') || file.name.endsWith('.xls'))
        : file.name.endsWith('.zip');
      
      if (isValidFile) {
        setUploadedFile(file);
        setError('');
      } else {
        const expectedFormat = importType === 'excel' ? 'Excel file (.xlsx or .xls)' : 'ZIP file';
        setError(`Please upload a ${expectedFormat}`);
      }
    }
  };

  const handleImport = async () => {
    if (!uploadedFile || !businessUnitId) return;

    // Clear previous results and errors
    setError('');
    setProcessingResults(null);
    setProcessingProgress('Starting import process...');

    try {
      // Simulate progress updates since we can't get real-time progress from the API
      const progressInterval = setInterval(() => {
        setProcessingProgress(prev => {
          if (prev.includes('Processing')) {
            return 'Processing files and extracting data...';
          } else if (prev.includes('extracting')) {
            return 'Creating farms and fields...';
          } else if (prev.includes('Creating')) {
            return 'Finalizing import...';
          } else {
            return 'Processing files and extracting data...';
          }
        });
      }, 2000);

      // Prepare form data for the API request
      const formData = new FormData();
      formData.append('file', uploadedFile);
      formData.append('business_unit_id', businessUnitId);

      setProcessingProgress('Uploading file to server...');

      // Make the actual API request
      const apiEndpoint = importType === 'excel' 
        ? `${API_BASE_URL}/excel/import`
        : `${API_BASE_URL}/john-deere/import`;
        
      const response = await fetch(apiEndpoint, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${userToken}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.text();
        throw new Error(`Server error: ${response.status} - ${errorData}`);
      }

      // Clear the progress interval
      clearInterval(progressInterval);

      const results = await response.json();
      console.log('Import completed successfully:', results);

      // If we reach here, import was successful
      setProcessingProgress('Import completed successfully!');

      // Set success message with actual results
      setProcessingResults({
        farmsCreated: results.farmsCreated || 'multiple',
        fieldsCreated: results.fieldsCreated || 'multiple'
      });

      // Close modal after a short delay to show success message
      setTimeout(() => {
        onClose();
        // Only refresh data after modal is closed to avoid the messy loading sequence
        if (onDataRefresh) {
          onDataRefresh();
        }
      }, 2000);

    } catch (error) {
      console.error('Import error:', error);
      setError(error instanceof Error ? error.message : 'Import failed');
      setProcessingProgress('');
    }
  };

  // Get the API endpoint based on import type
  const getApiEndpoint = () => {
    // Use the same endpoint for both since they use the same field names
    return '/john-deere/import';
  };

  const getTitle = () => {
    if (businessUnitName) {
      return `Import Farms & Fields for ${businessUnitName}`;
    }
    return 'Import Farms & Fields';
  };

  const getDescription = () => {
    let baseDesc = '';
    if (importType === 'excel') {
      baseDesc = 'Upload an Excel spreadsheet with columns: Farm, Field, Acres. Each row will create a field in the specified farm.';
    } else if (importType === 'caseAsf') {
      baseDesc = 'Upload your Case ASF Connect export file (ZIP format) to automatically import your farms and fields.';
    } else {
      baseDesc = 'Upload your John Deere Operation Center export file (ZIP format) to automatically import your farms and fields.';
    }

    if (businessUnitName) {
      return `${baseDesc} These will be associated with the business unit "${businessUnitName}".`;
    }
    return baseDesc;
  };

  return (
    <div className="modal-overlay import-farms-modal">
      <div className="modal-content import-farms-content">
        <div className="modal-header">
          <h2>{getTitle()}</h2>
          <button className="modal-close" onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className="import-content">
          <p>{getDescription()}</p>

          {/* Import Type Selection */}
          <div className="import-type-selection">
            <h3>Select Import Source</h3>
            <div className="radio-group">
              <label className="radio-option">
                <input
                  type="radio"
                  name="importType"
                  value="johnDeere"
                  checked={importType === 'johnDeere'}
                  onChange={(e) => setImportType(e.target.value as 'johnDeere' | 'caseAsf' | 'excel')}
                />
                <span className="radio-label">John Deere SHP File</span>
              </label>
              <label className="radio-option">
                <input
                  type="radio"
                  name="importType"
                  value="caseAsf"
                  checked={importType === 'caseAsf'}
                  onChange={(e) => setImportType(e.target.value as 'johnDeere' | 'caseAsf' | 'excel')}
                />
                <span className="radio-label">Case AFS Connect SHP File</span>
              </label>
              <label className="radio-option">
                <input
                  type="radio"
                  name="importType"
                  value="excel"
                  checked={importType === 'excel'}
                  onChange={(e) => setImportType(e.target.value as 'johnDeere' | 'caseAsf' | 'excel')}
                />
                <span className="radio-label">Excel Spreadsheet</span>
              </label>
            </div>
          </div>

          {/* File Upload Section */}
          <div className="file-upload-section">
            <div
              className={`file-upload-box ${isDragOver ? 'drag-over' : ''}`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <input
                type="file"
                id="importFile"
                accept={importType === 'excel' ? '.xlsx,.xls' : '.zip'}
                onChange={handleFileUpload}
                disabled={isProcessing}
                style={{ display: 'none' }}
              />
              <label htmlFor="importFile" className="file-upload-label">
                {uploadedFile ? (
                  <div className="file-selected">
                    <span className="file-name">📁 {uploadedFile.name}</span>
                    <span className="file-size">({(uploadedFile.size / 1024 / 1024).toFixed(2)} MB)</span>
                  </div>
                ) : (
                  <div className="file-upload-placeholder">
                    <span className="upload-icon">📁</span>
                    <span>Click to select {importType === 'excel' ? 'Excel file' : 'ZIP file'}</span>
                    <span className="upload-hint">or drag and drop here</span>
                  </div>
                )}
              </label>
            </div>

            {error && (
              <div className="error-message">
                <strong>Error:</strong> {error}
              </div>
            )}

            {processingProgress && (
              <div className="progress-message">
                {processingProgress}
              </div>
            )}

            {processingResults && (
              <div className="success-message">
                <strong>Import completed successfully!</strong>
                <br />
                Created {processingResults.farmsCreated} farms and {processingResults.fieldsCreated} fields.
              </div>
            )}
          </div>

          {/* Info Box */}
          <div className="info-box">
            <h4>What happens during import?</h4>
            {importType === 'excel' ? (
              <ul>
                <li>Your Excel file is securely uploaded and processed</li>
                <li>Each row creates a field with the specified farm name, field name, and acres</li>
                <li>Farms are created automatically if they don't exist</li>
                <li>Fields without boundaries will need boundaries added later</li>
                {businessUnitName && (
                  <li>Farms and fields will be associated with "{businessUnitName}"</li>
                )}
              </ul>
            ) : (
              <ul>
                <li>Your ZIP file is securely uploaded and processed</li>
                <li>Farm and field boundaries are extracted</li>
                <li>Area calculations are performed automatically</li>
                <li>All data is saved to your account</li>
                {businessUnitName && (
                  <li>Farms and fields will be associated with "{businessUnitName}"</li>
                )}
              </ul>
            )}
            {importType === 'excel' && (
              <div style={{ marginTop: '10px', padding: '10px', backgroundColor: '#f0f8ff', borderRadius: '4px', border: '1px solid #b3d9ff' }}>
                <strong>Excel Format Requirements:</strong>
                <br />
                • Column headers: <code>Farm</code>, <code>Field</code>, <code>Acres</code>
                <br />
                • Each row represents one field
                <br />
                • Acres should be numeric values
              </div>
            )}
          </div>
        </div>

        <div className="modal-actions">
          <button onClick={onClose} className="btn-secondary" disabled={isProcessing}>
            Cancel
          </button>
          <button
            onClick={handleImport}
            className="btn-primary"
            disabled={!uploadedFile || isProcessing || !!processingProgress}
          >
            {isProcessing || processingProgress ? 'Processing...' : 'Import Farms & Fields'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImportFarmsModal;
